import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowLeft, Star, Shield, Zap, Loader2, CheckCircle2, Play, ChevronDown, Trophy, CircleDashed, AlertTriangle, Printer } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Cell } from "recharts";
import { Button } from "@/components/ui/button";
import api from "@/lib/api";
import Avatar from "@/components/Avatar";
import HelpReport from "@/components/HelpReport";
import ReadingReport from "@/components/ReadingReport";
import { subjectIcon, AVAILABLE_SUBJECTS, isAvailable, reportSubject, levelLabel } from "@/lib/subjects";

const T = {
  en: {
    dashboard: "Dashboard", download: "Download report", kid: "Kid mode", grade: (g) => `Grade ${g}`,
    stars: "Stars earned", shields: "Shields won", xp: "Total XP", bySubject: "Progress by subject",
    levels: (a, b) => `${a}/${b} levels`, recent: "Recent activity", noActivity: "No activity yet. Jump into kid mode to get started!",
    level: (n) => `Level ${n}`, scored: (a, b) => `Scored ${a}/${b}`, chapterResults: "chapter results", achievedOf: (a, b) => `${a}/${b} achieved`,
    tap: "Tap a chapter to see the accuracy of every attempt per level.", help: "Needs a little help",
    under: (l) => `Still under 60% after a few tries: ${l}.`, notStarted: (n) => `${n} chapter(s) not started yet.`,
    levelsAchieved: (n) => `${n}/3 levels achieved`, notStartedOne: "Not started", achieved: "Achieved", needsHelp: "Needs help",
    inProgress: "In progress", new: "New", best: (p) => `Best ${p}%`, try: (k, p) => `Try ${k}: ${p}%`, noAttempts: "No attempts yet",
    reportTitle: "Progress report", chaptersAchieved: (a, b) => `${a}/${b} chapters achieved`, needsHelpPrint: "Needs help (under 60% after a few tries)",
    noAttemptsPrint: "No attempts recorded yet.", bestPrint: (p) => `best ${p}%`, achievedPrint: "Achieved", print: "Print / Save as PDF",
    badges: (s, sh, x) => `⭐ ${s} stars &nbsp; 🛡️ ${sh} shields &nbsp; ⚡ ${x} XP`,
  },
  fr: {
    dashboard: "Tableau de bord", download: "Télécharger le rapport", kid: "Mode enfant", grade: (g) => `Grade ${g}`,
    stars: "Étoiles gagnées", shields: "Boucliers gagnés", xp: "XP total", bySubject: "Progrès par matière",
    levels: (a, b) => `${a}/${b} niveaux`, recent: "Activité récente", noActivity: "Pas encore d'activité. Passez en mode enfant pour commencer !",
    level: (n) => `Niveau ${n}`, scored: (a, b) => `Score ${a}/${b}`, chapterResults: "résultats par chapitre", achievedOf: (a, b) => `${a}/${b} réussis`,
    tap: "Touchez un chapitre pour voir le score de chaque essai par niveau.", help: "A besoin d'un petit coup de main",
    under: (l) => `Toujours sous 60 % après plusieurs essais : ${l}.`, notStarted: (n) => `${n} chapitre(s) pas encore commencé(s).`,
    levelsAchieved: (n) => `${n}/3 niveaux réussis`, notStartedOne: "Pas commencé", achieved: "Réussi", needsHelp: "Besoin d'aide",
    inProgress: "En cours", new: "Nouveau", best: (p) => `Meilleur ${p} %`, try: (k, p) => `Essai ${k} : ${p} %`, noAttempts: "Pas encore d'essai",
    reportTitle: "Rapport de progrès", chaptersAchieved: (a, b) => `${a}/${b} chapitres réussis`, needsHelpPrint: "Besoin d'aide (sous 60 % après plusieurs essais)",
    noAttemptsPrint: "Aucun essai enregistré pour l'instant.", bestPrint: (p) => `meilleur ${p} %`, achievedPrint: "Réussi", print: "Imprimer / Enregistrer en PDF",
    badges: (s, sh, x) => `⭐ ${s} étoiles &nbsp; 🛡️ ${sh} boucliers &nbsp; ⚡ ${x} XP`,
  },
};

const chTitle = (c, lang) => (lang === "fr" && c.title_fr) || c.title;

function LangToggle({ lang, setLang }) {
  return (
    <div className="flex rounded-full border border-slate-300 overflow-hidden bg-white" data-testid="report-lang-switch">
      {["en", "fr"].map((l) => (
        <button key={l} onClick={() => setLang(l)} data-testid={`report-lang-${l}`}
          className={`px-3 h-9 text-xs font-bold transition-colors ${lang === l ? "bg-slate-900 text-white" : "text-slate-600 hover:bg-slate-50"}`}>
          {l.toUpperCase()}
        </button>
      ))}
    </div>
  );
}

export default function ChildProgress() {
  const { childId } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [reports, setReports] = useState([]);
  const [lang, setLangState] = useState(() => (localStorage.getItem("aa_report_lang") === "fr" ? "fr" : "en"));
  const setLang = (l) => { setLangState(l); localStorage.setItem("aa_report_lang", l); };
  const t = T[lang];
  const loc = lang === "fr" ? "fr-FR" : undefined;

  useEffect(() => {
    api.get(`/children/${childId}/progress`).then(({ data }) => setData(data)).catch(() => navigate("/parent"));
  }, [childId, navigate]);

  useEffect(() => {
    if (!data?.child) return;
    const grade = data.child.grade;
    const subs = AVAILABLE_SUBJECTS.filter((name) => isAvailable(grade, name));
    Promise.all(subs.map((name) =>
      api.get(`/children/${childId}/report/${encodeURIComponent(name)}`).then(({ data }) => data).catch(() => null)
    )).then((res) => setReports(res.filter(Boolean)));
  }, [data, childId]);

  if (!data) {
    return <div className="min-h-screen flex items-center justify-center bg-[#F8FAFC]"><Loader2 className="animate-spin text-blue-600" size={32} /></div>;
  }

  const { child, recent } = data;
  const subjects = data.subjects.filter((s) => s.total > 0);
  const chartData = subjects.map((s) => ({ name: reportSubject(s.subject, lang).split(" ")[0], percent: s.percent, color: s.color }));

  const printReport = () => {
    const esc = (s) => String(s ?? "").replace(/[&<>]/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;" }[c]));
    const chipColor = (p, passed) => (passed || p >= 60) ? "#15803D" : p >= 40 ? "#B45309" : "#B91C1C";
    const chipBg = (p, passed) => (passed || p >= 60) ? "#DCFCE7" : p >= 40 ? "#FEF3C7" : "#FEE2E2";
    const subjRows = subjects.map((s) => `<tr><td>${esc(reportSubject(s.subject, lang))}</td><td>${t.levels(s.completed, s.total)}</td><td style="text-align:right;font-weight:700">${s.percent}%</td></tr>`).join("");
    const reportBlocks = reports.map((rep) => {
      const achieved = rep.chapters.filter((c) => c.achieved).length;
      const weak = rep.chapters.filter(isWeakChapter);
      const chapters = rep.chapters.filter((c) => c.levels.some((l) => l.attempts_count > 0)).map((c) => {
        const levels = c.levels.map((l) => {
          const chips = l.attempts.map((a, k) => `<span style="display:inline-block;margin:2px;padding:2px 6px;border-radius:5px;font-size:11px;background:${chipBg(a.percent, a.passed)};color:${chipColor(a.percent, a.passed)}">${esc(t.try(k + 1, a.percent))} <small style="opacity:.7">${esc(fmtDate(a.at, loc))}</small></span>`).join("");
          const status = l.achieved ? "✅" : (isWeakLevel(l) ? "⚠️" : "");
          return l.attempts_count > 0 ? `<div style="margin:6px 0"><b>${esc(levelLabel(l.name, lang))}</b> ${status} <span style="color:#64748b">— ${esc(t.bestPrint(l.best_percent))}</span><br/>${chips}</div>` : "";
        }).join("");
        return `<div style="border:1px solid #e2e8f0;border-radius:8px;padding:10px;margin:8px 0"><div style="font-weight:700">${c.index + 1}. ${esc(chTitle(c, lang))} ${c.achieved ? `🏆 ${t.achievedPrint}` : ""}</div>${levels}</div>`;
      }).join("");
      return `<h2 style="margin-top:22px">${esc(reportSubject(rep.subject, lang))} — ${t.chaptersAchieved(achieved, rep.chapters.length)}</h2>${weak.length ? `<p style="color:#b91c1c">${t.needsHelpPrint} : ${weak.map((c) => esc(chTitle(c, lang))).join(", ")}.</p>` : ""}${chapters || `<p style='color:#64748b'>${t.noAttemptsPrint}</p>`}`;
    }).join("");
    const html = `<!doctype html><html lang="${lang}"><head><meta charset="utf-8"><title>${esc(child.name)} — ${t.reportTitle}</title>
      <style>body{font-family:system-ui,Arial,sans-serif;color:#0f172a;max-width:800px;margin:24px auto;padding:0 20px}
      h1{margin:0}table{width:100%;border-collapse:collapse;margin-top:10px}td{padding:6px 4px;border-bottom:1px solid #eee;font-size:14px}
      .muted{color:#64748b;font-size:13px}.badges{margin-top:8px;font-size:14px}
      @media print{button{display:none}}</style></head><body>
      <div style="display:flex;justify-content:space-between;align-items:center">
        <div><h1>Anou Appran</h1><p class="muted">${t.reportTitle} · ${esc(child.name)} · ${t.grade(esc(child.grade))}</p></div>
        <p class="muted">${new Date().toLocaleDateString(loc, { day: "numeric", month: "long", year: "numeric" })}</p>
      </div>
      <p class="badges">${t.badges(child.stars, child.shields, child.xp)}</p>
      <h2>${t.bySubject}</h2><table>${subjRows}</table>
      ${reportBlocks}
      <button onclick="window.print()" style="margin-top:24px;padding:10px 18px;border:0;border-radius:8px;background:#2563eb;color:#fff;font-size:14px;cursor:pointer">${t.print}</button>
      </body></html>`;
    const w = window.open("", "_blank");
    if (!w) return;
    w.document.write(html);
    w.document.close();
    setTimeout(() => { try { w.focus(); w.print(); } catch (e) {} }, 400);
  };

  return (
    <div className="min-h-screen bg-[#F8FAFC] font-parent text-slate-900">
      <header className="sticky top-0 z-40 backdrop-blur-xl bg-white/80 border-b border-slate-200">
        <div className="max-w-5xl mx-auto px-5 min-h-16 py-2 flex items-center justify-between gap-2 flex-wrap">
          <Button variant="ghost" onClick={() => navigate("/parent")} className="rounded-full" data-testid="back-to-parent">
            <ArrowLeft size={18} className="mr-1" /> {t.dashboard}
          </Button>
          <div className="flex items-center gap-2 flex-wrap">
            <LangToggle lang={lang} setLang={setLang} />
            <Button variant="outline" onClick={printReport} className="rounded-full" data-testid="print-report-btn">
              <Printer size={16} className="mr-1" /> {t.download}
            </Button>
            <Button onClick={() => navigate(`/kid/${child.id}`)} className="rounded-full bg-blue-600 hover:bg-blue-700" data-testid="enter-kid-mode">
              <Play size={16} className="mr-1" /> {t.kid}
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-5 py-8">
        <div className="flex items-center gap-4">
          <Avatar avatar={child.avatar} size={64} />
          <div>
            <h1 className="text-3xl font-bold tracking-tight">{child.name}</h1>
            <p className="text-slate-500">{t.grade(child.grade)}</p>
          </div>
        </div>

        <div className="grid grid-cols-3 gap-4 mt-6">
          <Metric icon={Star} color="#EAB308" value={child.stars} label={t.stars} />
          <Metric icon={Shield} color="#3B82F6" value={child.shields} label={t.shields} />
          <Metric icon={Zap} color="#10B981" value={child.xp} label={t.xp} />
        </div>

        <HelpReport childId={child.id} name={child.name} lang={lang} />
        <ReadingReport childId={child.id} lang={lang} />

        <div className="rounded-2xl border border-slate-200 bg-white shadow-sm p-6 mt-6">
          <h2 className="font-bold text-lg mb-4" data-testid="progress-by-subject-title">{t.bySubject}</h2>
          <div className="h-56">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={chartData} margin={{ top: 8, right: 8, left: -20, bottom: 0 }}>
                <XAxis dataKey="name" tick={{ fontSize: 12, fill: "#64748B" }} axisLine={false} tickLine={false} />
                <YAxis domain={[0, 100]} tick={{ fontSize: 12, fill: "#64748B" }} axisLine={false} tickLine={false} />
                <Bar dataKey="percent" radius={[8, 8, 0, 0]}>
                  {chartData.map((e, i) => <Cell key={i} fill={e.color} />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
          <div className="grid sm:grid-cols-2 gap-3 mt-4">
            {subjects.map((s) => {
              const Icon = subjectIcon(s.subject);
              return (
                <div key={s.subject} className="flex items-center gap-3 rounded-xl bg-slate-50 p-3" data-testid={`subject-progress-${s.subject}`}>
                  <div className="w-9 h-9 rounded-lg flex items-center justify-center" style={{ background: s.color }}>
                    <Icon color="#fff" size={18} />
                  </div>
                  <div className="flex-1">
                    <p className="text-sm font-semibold">{reportSubject(s.subject, lang)}</p>
                    <p className="text-xs text-slate-500">{t.levels(s.completed, s.total)} · {s.stars} ⭐</p>
                  </div>
                  <span className="text-sm font-bold" style={{ color: s.color }}>{s.percent}%</span>
                </div>
              );
            })}
          </div>
        </div>

        {reports.map((rep) => (
          <ChapterReport key={rep.subject} report={rep} lang={lang} />
        ))}

        <div className="rounded-2xl border border-slate-200 bg-white shadow-sm p-6 mt-6">
          <h2 className="font-bold text-lg mb-4">{t.recent}</h2>
          {recent.length === 0 ? (
            <p className="text-sm text-slate-500">{t.noActivity}</p>
          ) : (
            <div className="space-y-2">
              {recent.map((r, i) => (
                <motion.div key={i} initial={{ opacity: 0, x: -8 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: i * 0.04 }}
                  className="flex items-center gap-3 py-2 border-b border-slate-100 last:border-0">
                  <CheckCircle2 size={18} className={r.completed ? "text-green-500" : "text-slate-300"} />
                  <div className="flex-1">
                    <p className="text-sm font-medium">{reportSubject(r.subject, lang)} · {t.level(r.level)}</p>
                    <p className="text-xs text-slate-400">{t.scored(r.score, r.total)}</p>
                  </div>
                  <span className="text-sm">{"⭐".repeat(r.stars) || "—"}</span>
                </motion.div>
              ))}
            </div>
          )}
        </div>
      </main>
    </div>
  );
}

function ChapterReport({ report, lang }) {
  const t = T[lang];
  const Icon = subjectIcon(report.subject);
  const achievedCount = report.chapters.filter((c) => c.achieved).length;
  const startedChapters = report.chapters.filter((c) => c.levels.some((l) => l.attempts_count > 0));
  const notStarted = report.chapters.length - startedChapters.length;
  const weakChapters = report.chapters.filter(isWeakChapter);
  return (
    <div className="rounded-2xl border border-slate-200 bg-white shadow-sm p-6 mt-6" data-testid={`chapter-report-${report.subject}`}>
      <div className="flex items-center justify-between mb-1 gap-2 flex-wrap">
        <h2 className="font-bold text-lg flex items-center gap-2">
          <span className="w-7 h-7 rounded-lg flex items-center justify-center" style={{ background: report.color }}>
            <Icon color="#fff" size={16} />
          </span>
          {reportSubject(report.subject, lang)} — {t.chapterResults}
        </h2>
        <span className="text-sm font-semibold text-green-600 flex items-center gap-1">
          <Trophy size={15} /> {t.achievedOf(achievedCount, report.chapters.length)}
        </span>
      </div>
      <p className="text-xs text-slate-400 mb-4">{t.tap}</p>

      {weakChapters.length > 0 && (
        <div className="rounded-xl border border-red-200 bg-red-50 p-3 mb-4" data-testid="focus-areas">
          <p className="text-sm font-semibold text-red-700 flex items-center gap-1.5">
            <AlertTriangle size={15} /> {t.help}
          </p>
          <p className="text-xs text-red-600 mt-1">{t.under(weakChapters.map((c) => chTitle(c, lang)).join(", "))}</p>
        </div>
      )}

      <div className="space-y-2">
        {report.chapters.map((ch) => (
          <ChapterRow key={ch.chapter_id} chapter={ch} lang={lang} />
        ))}
      </div>
      {notStarted > 0 && (
        <p className="text-xs text-slate-400 mt-3">{t.notStarted(notStarted)}</p>
      )}
    </div>
  );
}

function pctColor(p, passed) {
  if (passed || p >= 60) return { bg: "#DCFCE7", fg: "#15803D", border: "#86EFAC" };
  if (p >= 40) return { bg: "#FEF3C7", fg: "#B45309", border: "#FCD34D" };
  return { bg: "#FEE2E2", fg: "#B91C1C", border: "#FCA5A5" };
}

function fmtDate(iso, loc) {
  if (!iso) return "";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "";
  return `${d.toLocaleDateString(loc, { day: "numeric", month: "short" })} · ${d.toLocaleTimeString(loc, { hour: "2-digit", minute: "2-digit" })}`;
}

// A level is a weak spot if the child tried it 2+ times and still hasn't reached the 60% pass mark.
function isWeakLevel(l) {
  return l.attempts_count >= 2 && !l.achieved && l.best_percent < 60;
}
function isWeakChapter(ch) {
  return !ch.achieved && ch.levels.some(isWeakLevel);
}

function StatusPill({ chapter, started, weak, t }) {
  if (chapter.achieved) {
    return <span className="inline-flex items-center gap-1 text-xs font-semibold text-green-700 bg-green-50 border border-green-200 px-2 py-1 rounded-full"><Trophy size={12} /> {t.achieved}</span>;
  }
  if (weak) {
    return <span className="inline-flex items-center gap-1 text-xs font-semibold text-red-700 bg-red-50 border border-red-200 px-2 py-1 rounded-full" data-testid={`needs-help-${chapter.chapter_id}`}><AlertTriangle size={12} /> {t.needsHelp}</span>;
  }
  if (started) {
    return <span className="inline-flex items-center gap-1 text-xs font-semibold text-amber-700 bg-amber-50 border border-amber-200 px-2 py-1 rounded-full">{t.inProgress}</span>;
  }
  return <span className="inline-flex items-center gap-1 text-xs font-semibold text-slate-400 bg-slate-50 border border-slate-200 px-2 py-1 rounded-full"><CircleDashed size={12} /> {t.new}</span>;
}

function ChapterRow({ chapter, lang }) {
  const t = T[lang];
  const loc = lang === "fr" ? "fr-FR" : undefined;
  const [open, setOpen] = useState(false);
  const achievedLevels = chapter.levels.filter((l) => l.achieved).length;
  const started = chapter.levels.some((l) => l.attempts_count > 0);
  const weak = isWeakChapter(chapter);
  return (
    <div className={`rounded-xl border overflow-hidden ${weak ? "border-red-200" : "border-slate-200"}`}>
      <button
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center gap-3 p-3 text-left hover:bg-slate-50 transition-colors"
        data-testid={`chapter-row-${chapter.chapter_id}`}
      >
        <span className="w-7 h-7 shrink-0 rounded-full bg-slate-100 flex items-center justify-center text-xs font-bold text-slate-500">
          {chapter.index + 1}
        </span>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold truncate" data-testid={`chapter-row-title-${chapter.chapter_id}`}>{chTitle(chapter, lang)}</p>
          <p className="text-xs text-slate-400">{started ? t.levelsAchieved(achievedLevels) : t.notStartedOne}</p>
        </div>
        <StatusPill chapter={chapter} started={started} weak={weak} t={t} />
        <ChevronDown size={18} className={`text-slate-400 shrink-0 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      <AnimatePresence initial={false}>
        {open && (
          <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }}
            className="overflow-hidden border-t border-slate-100">
            <div className="p-3 space-y-3 bg-slate-50/60">
              {chapter.levels.map((lv) => (
                <div key={lv.level} data-testid={`level-report-${chapter.chapter_id}-${lv.level}`}>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-semibold">{levelLabel(lv.name, lang)}</span>
                      {lv.achieved && <CheckCircle2 size={15} className="text-green-500" />}
                      {isWeakLevel(lv) && <AlertTriangle size={14} className="text-red-500" />}
                      {lv.best_stars > 0 && <span className="text-xs">{"⭐".repeat(lv.best_stars)}</span>}
                    </div>
                    <span className="text-xs font-semibold text-slate-500">
                      {lv.attempts_count > 0 ? t.best(lv.best_percent) : "—"}
                    </span>
                  </div>
                  {lv.attempts_count > 0 ? (
                    <div className="flex flex-wrap gap-1.5 mt-2">
                      {lv.attempts.map((a, k) => {
                        const c = pctColor(a.percent, a.passed);
                        return (
                          <span key={k}
                            className="inline-flex flex-col items-center px-2 py-1 rounded-md border leading-tight"
                            style={{ background: c.bg, color: c.fg, borderColor: c.border }}
                            data-testid={`attempt-chip-${chapter.chapter_id}-${lv.level}-${k}`}>
                            <span className="text-[11px] font-bold">{t.try(k + 1, a.percent)}</span>
                            <span className="text-[9px] opacity-80">{fmtDate(a.at, loc)}</span>
                          </span>
                        );
                      })}
                    </div>
                  ) : (
                    <p className="text-xs text-slate-400 mt-1">{t.noAttempts}</p>
                  )}
                </div>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

function Metric({ icon: Icon, color, value, label }) {
  return (
    <div className="rounded-2xl border border-slate-200 bg-white shadow-sm p-5 text-center">
      <div className="w-11 h-11 rounded-xl mx-auto flex items-center justify-center mb-2" style={{ background: `${color}22` }}>
        <Icon size={22} style={{ color }} />
      </div>
      <p className="text-2xl font-bold">{value}</p>
      <p className="text-xs text-slate-400 uppercase tracking-wide">{label}</p>
    </div>
  );
}
